import React from 'react';
import { colors } from '../../styles/tokens';
import { useEsMovil } from '../../hooks/useEsMovil';
import {
  bloqueConObjetivo,
  colorCumplimiento,
  colorVariacion,
  fmtDiferencia,
  fmtLugar,
  fmtValor,
  fmtVariacion,
  sinSemaforo,
} from '../../utils/scorecard';
import { fmtPct } from '../../utils/format';
import type { KpiScorecard } from '../../types/scorecard.types';
import TendenciaSemanal from './TendenciaSemanal';

export interface TablaObjRealProps {
  titulo: string;
  kpis: KpiScorecard[];
  ver: 'semana' | 'acumulado';
}

/** Insignia de estado: «En validación» (ámbar) o «No aplica» (gris). */
export const InsigniaEstado: React.FC<{ kpi: KpiScorecard }> = ({ kpi }) => {
  if (kpi.estado !== 'en_validacion' && kpi.estado !== 'no_aplica') return null;
  const validacion = kpi.estado === 'en_validacion';
  return (
    <span
      data-testid={`insignia-${kpi.clave}`}
      style={{
        fontSize: 10.5,
        fontWeight: 800,
        color: validacion ? '#92400e' : colors.texto.secondary,
        background: validacion ? '#fef3c7' : colors.chipBg,
        border: `1px solid ${validacion ? '#fde68a' : colors.border}`,
        borderRadius: 999,
        padding: '1px 8px',
        whiteSpace: 'nowrap',
      }}
    >
      {validacion ? 'En validación' : 'No aplica'}
    </span>
  );
};

const th: React.CSSProperties = {
  textAlign: 'right',
  fontSize: 10.5,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: colors.texto.quaternary,
  fontWeight: 800,
  padding: '8px 10px',
  borderBottom: `1px solid ${colors.rowBorder}`,
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = {
  textAlign: 'right',
  padding: '10px 10px',
  borderBottom: `1px solid ${colors.rowBorder}`,
  fontVariantNumeric: 'tabular-nums',
  fontSize: 13.5,
  fontWeight: 700,
  color: colors.texto.strong,
  verticalAlign: 'middle',
};

/**
 * La tabla de un bloque: Indicador │ Objetivo │ Real │ Diferencia │
 * Cumplimiento │ Vs año anterior │ Lugar │ Tendencia. Si ningún KPI del bloque
 * tiene objetivo, las tres columnas de objetivo se quitan y una nota lo dice.
 *
 * En teléfono, una TARJETA por KPI (regla del módulo: una tabla de más de 3
 * columnas nunca se arrastra de lado).
 *
 * El lugar es SEMANAL: en la vista «Acumulado» la columna no aparece.
 */
const TablaObjReal: React.FC<TablaObjRealProps> = ({ titulo, kpis, ver }) => {
  const esMovil = useEsMovil();
  const conObjetivo = bloqueConObjetivo(kpis, ver);
  const conLugar = ver === 'semana';

  if (esMovil) {
    return (
      <div style={{ display: 'grid', gap: 10 }} data-testid={`tarjetas-${titulo}`}>
        {kpis.map((k) => {
          const m = k[ver];
          const gris = sinSemaforo(k);
          return (
            <div
              key={k.clave}
              data-testid={`kpi-${k.clave}`}
              style={{
                border: `1px solid ${colors.border}`,
                borderRadius: 14,
                padding: '12px 14px',
                background: colors.card,
                minWidth: 0,
                overflowWrap: 'anywhere',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontWeight: 800, fontSize: 14 }}>{k.rotulo}</span>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: gris ? colors.texto.tertiary : colorVariacion(m.variacionAA, k.polaridad) }}>
                  {m.variacionAA !== null ? `${fmtVariacion(m.variacionAA)} vs AA` : ''}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                <span style={{ fontSize: 22, fontWeight: 800 }}>{fmtValor(m.real, k.unidad)}</span>
                {m.cumplimiento !== null && (
                  <span style={{ fontSize: 13, fontWeight: 800, color: gris ? colors.texto.tertiary : colorCumplimiento(m.cumplimiento, k.polaridad) }}>
                    Cumplimiento {fmtPct(m.cumplimiento)}
                  </span>
                )}
                <InsigniaEstado kpi={k} />
              </div>
              {m.objetivo !== null && (
                <div style={{ fontSize: 12.5, color: colors.texto.secondary, marginTop: 4 }}>
                  Objetivo {fmtValor(m.objetivo, k.unidad)} · Diferencia {fmtDiferencia(m.diferencia, k.unidad)}
                </div>
              )}
              {conLugar && (k.lugar.cadena || k.lugar.regional) && (
                <div style={{ fontSize: 12.5, color: colors.texto.secondary, marginTop: 2 }}>
                  Lugar {fmtLugar(k.lugar.cadena)} en su cadena · {fmtLugar(k.lugar.regional)} en su regional
                </div>
              )}
              {k.motivo && <div style={{ fontSize: 12, color: colors.texto.tertiary, marginTop: 4 }}>{k.motivo}</div>}
              <div style={{ marginTop: 8 }}>
                <TendenciaSemanal kpi={k} ancho={300} alto={34} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid={`tabla-${titulo}`}>
        <caption style={{ position: 'absolute', left: -9999 }}>{titulo}</caption>
        <thead>
          <tr>
            <th scope="col" style={{ ...th, textAlign: 'left', paddingLeft: 0 }}>
              Indicador
            </th>
            {conObjetivo && (
              <th scope="col" style={th}>
                Objetivo
              </th>
            )}
            <th scope="col" style={th}>
              Real
            </th>
            {conObjetivo && (
              <th scope="col" style={th}>
                Diferencia
              </th>
            )}
            {conObjetivo && (
              <th scope="col" style={th} title="Real ÷ Objetivo">
                Cumplimiento
              </th>
            )}
            <th scope="col" style={th} title="Real ÷ misma semana del año anterior − 1">
              Vs año anterior
            </th>
            {conLugar && (
              <th scope="col" style={th} title="Lugar en su cadena (y en su regional) en este indicador; 1 es el mejor">
                Lugar
              </th>
            )}
            <th scope="col" style={{ ...th, textAlign: 'center' }}>
              Tendencia
            </th>
          </tr>
        </thead>
        <tbody>
          {kpis.map((k) => {
            const m = k[ver];
            const gris = sinSemaforo(k);
            return (
              <tr key={k.clave} data-testid={`kpi-${k.clave}`}>
                <th scope="row" style={{ ...td, textAlign: 'left', paddingLeft: 0, fontWeight: 700 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span title={k.definicion}>{k.rotulo}</span>
                    <InsigniaEstado kpi={k} />
                    {k.cuentaEnRanking && ver === 'semana' && (
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: colors.texto.quaternary }}>peso {k.peso}</span>
                    )}
                  </span>
                  {k.motivo && (
                    <span style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: colors.texto.tertiary, marginTop: 2 }}>
                      {k.motivo}
                    </span>
                  )}
                </th>
                {conObjetivo && <td style={td}>{fmtValor(m.objetivo, k.unidad)}</td>}
                <td style={{ ...td, fontWeight: 800 }}>{fmtValor(m.real, k.unidad)}</td>
                {conObjetivo && <td style={td}>{fmtDiferencia(m.diferencia, k.unidad)}</td>}
                {conObjetivo && (
                  <td style={{ ...td, color: gris ? colors.texto.tertiary : colorCumplimiento(m.cumplimiento, k.polaridad) }}>
                    {m.cumplimiento === null ? '—' : fmtPct(m.cumplimiento)}
                  </td>
                )}
                <td
                  style={{ ...td, color: gris ? colors.texto.tertiary : colorVariacion(m.variacionAA, k.polaridad) }}
                  title={m.anterior !== null ? `Año anterior: ${fmtValor(m.anterior, k.unidad)}` : undefined}
                >
                  {fmtVariacion(m.variacionAA)}
                </td>
                {conLugar && (
                  <td style={td} title={k.lugar.regional ? `En su regional: ${fmtLugar(k.lugar.regional)}` : undefined}>
                    {fmtLugar(k.lugar.cadena)}
                  </td>
                )}
                <td style={{ ...td, textAlign: 'center' }}>
                  <span style={{ display: 'inline-block' }}>
                    <TendenciaSemanal kpi={k} />
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!conObjetivo && (
        <p style={{ fontSize: 12, color: colors.texto.tertiary, margin: '8px 0 0' }}>
          Sin objetivo: se compara contra la misma semana del año anterior.
        </p>
      )}
    </div>
  );
};

export default TablaObjReal;
